import { useMemo, type RefObject } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';
import { VisualizationContainer } from 'tabview/ViewContainer';
import { useResizeObserver } from '@ardoq/hooks';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import { colors } from '@ardoq/design-tokens';
import type {
  FormatNodeValue,
  SizedNode,
  TreemapHierarchyNode,
} from '../../types';
import { buildHierarchy } from '../buildHierarchy';
import { toLeafText } from '../standard/toLeafText';
import { wrapWords } from '../wrapWords';
import { rankLabelLines } from '../rankLabelLines';
import { legibleLines } from '../legibleLines';
import {
  measureSvgLabel,
  SVG_LABEL_LINE_HEIGHT,
  SVG_LABEL_SIZE,
  toTextColor,
} from '../svgLabels';
import { toPartitionRadius } from './toPartitionRadius';
import { t, type I18nLocale } from '@ardoq/i18n';
import { toAccessibleName, toNavigableNodes } from '../hierarchyNavigation';
import { useHierarchyNavigation } from '../useHierarchyNavigation';
import { toDrawnOrder } from '../toDrawnOrder';

const ARC_PADDING = 1;

const arcInnerRadius = ({ y0 }: TreemapHierarchyNode) => y0;
const arcOuterRadius = ({ y1 }: TreemapHierarchyNode) => y1 - ARC_PADDING;
const arc = (diameter: number) =>
  d3
    .arc<d3.HierarchyRectangularNode<SizedNode>>()
    .startAngle(({ x0 }) => x0)
    .endAngle(({ x1 }) => x1)
    .padAngle(({ x0, x1 }) =>
      Math.min((x1 - x0) / 2, (2 * ARC_PADDING) / diameter)
    )
    .innerRadius(arcInnerRadius)
    .outerRadius(arcOuterRadius);

/**
 * `d3.partition` spreads the whole tree's depth across the radius it is given, so drawing only
 * the first few rings of a deeper tree left the rest of the pane empty. Asking for a radius
 * scaled by how much of the depth is drawn puts those rings back out at the edge.
 */
const layout = (diameter: number, totalDepth: number, drawnDepth: number) =>
  d3
    .partition<SizedNode>()
    .size([
      2 * Math.PI,
      toPartitionRadius({ diameter, totalDepth, drawnDepth }),
    ]);

/* The stroke is set here rather than inline beside the fill, so the focus ring can replace it:
   an inline `style` beats a stylesheet rule whatever its specificity. See
   `HierarchicalPackingViewNode` for why a stroke and not an `outline`. */
const Arc = styled.path`
  stroke: ${colors.borderSubtle};

  &:focus-visible {
    outline: none;
    stroke: ${colors.borderFocus};
    stroke-width: 2;
  }
`;

const Label = styled.text`
  text-anchor: middle;
  dominant-baseline: middle;
  font: ${SVG_LABEL_SIZE}px ${ARDOQ_DEFAULT_FONT_FAMILY};
  pointer-events: none;
`;

export type SunburstVisualizationProps = {
  sizedNodes: SizedNode[];
  maxDepth: number;
  formatValue: FormatNodeValue;
  viewContainer: RefObject<HTMLDivElement | null>;
  i18nLocale: I18nLocale;
};

/**
 * The sunburst itself, with nothing about which mode is drawing it.
 *
 * Split out so each mode's view wraps it in its own shell. See
 * {@link HierarchicalTreemapVisualization} for why the split is here rather than at the pixel.
 */
export const SunburstVisualization = ({
  sizedNodes,
  maxDepth,
  formatValue,
  viewContainer,
  i18nLocale,
}: SunburstVisualizationProps) => {
  const { width = 0, height = 0 } = useResizeObserver(viewContainer);
  const diameter = Math.min(width, height);
  /* Build and layout in one memo: `d3.partition` writes onto the nodes it is given, so the memo
     owning the build is what keeps the mutation to what it made. `maxDepth` is a dependency here
     where it is not for the treemap - `drawnDepth` feeds `toPartitionRadius`. */
  const sunburstRootNode = useMemo(() => {
    const hierarchy = buildHierarchy(sizedNodes);
    const totalDepth = Math.max(1, hierarchy.height);
    const drawnDepth =
      maxDepth <= 0 ? totalDepth : Math.min(maxDepth, totalDepth);
    return layout(diameter, totalDepth, drawnDepth)(hierarchy);
  }, [sizedNodes, diameter, maxDepth]);
  const getArc = arc(diameter);

  /* Memoized for the reason `HierarchicalTreemapVisualization` gives: a fresh array every render
     makes `useHierarchyNavigation`'s memos dead and re-attaches every arc's ref. */
  const descendants = useMemo(
    () => toDrawnOrder(sunburstRootNode, maxDepth),
    [sunburstRootNode, maxDepth]
  );

  const navigableNodes = useMemo(
    () => toNavigableNodes(descendants),
    [descendants]
  );
  const { treeProps, toNodeProps } = useHierarchyNavigation(
    navigableNodes,
    t(i18nLocale, 'tabview:hierarchicalTreemap.sunburstTreeLabel', 'Sunburst')
  );

  return (
    <VisualizationContainer
      data-context-menu={OBJECT_CONTEXT_MENU_NAME}
      ref={viewContainer}
    >
      <svg style={{ height: '100%', width: '100%' }} {...treeProps}>
        <g transform={`translate(${width / 2}, ${height / 2})`}>
          {descendants.map((node, index) => (
            <Arc
              key={node.data.id}
              className="component"
              style={{ fill: node.data.color ?? colors.surfaceDefault }}
              data-component-id={node.data.modelId ?? undefined}
              d={getArc(node) ?? undefined}
              {...toNodeProps(
                navigableNodes[index],
                toAccessibleName(node.data.label, formatValue(node.value))
              )}
            />
          ))}
          {/* Already spoken by each arc's `aria-label`, so hidden rather than read twice.
              On the group rather than per label, so a label the geometry declines to draw
              cannot be the one that was left exposed. */}
          <g aria-hidden="true" data-testid="sunburst-labels">
            {descendants.map(node => {
              const {
                x0,
                x1,
                y0,
                y1,
                value,
                data: { id, label, isDark },
              } = node;
              const x = (((x0 + x1) / 2) * 180) / Math.PI;
              const y = (y0 + y1) / 2;
              const labelTransform = `rotate(${
                x - 90
              }) translate(${y}, 0) rotate(${x < 180 ? 0 : 180})`;
              /* A label runs outward along the radius, so the ring's thickness is what it has
               to fit in — but the wedge's arc at that radius is what it has to fit *between*.
               A wedge narrower than the text is tall cannot hold a label without it spilling
               over both edges onto whatever is behind, which is where the stray white labels
               floating off the chart came from. */
              if ((x1 - x0) * y < SVG_LABEL_SIZE) return null;
              /* One line, along the ring rather than across it: what a label has to fit in is
               the ring's thickness, and the value is reserved out of it first so a thin ring
               shows the number rather than a truncated name. */
              const valueLabel = `(${formatValue(value)})`;
              const availableWidth =
                arcOuterRadius(node) - arcInnerRadius(node) - ARC_PADDING;
              /* A ring with no thickness says nothing, and `toLeafText` answers an unmeasured
               shape with the whole untruncated label — right for the treemap, which clips it,
               and wrong here, where an arc's `<text>` is not clipped by anything and would be
               drawn straight across the chart. Packing filters the same case out. */
              if (availableWidth <= 0) return null;
              const lines = rankLabelLines({
                label,
                valueLabel,
                withValue: toLeafText({
                  label: label ?? '',
                  valueLabel,
                  availableWidth,
                  availableHeight: SVG_LABEL_LINE_HEIGHT,
                  measure: measureSvgLabel,
                  lineHeight: SVG_LABEL_LINE_HEIGHT,
                  wrap: wrapWords,
                }),
                /* Through `legibleLines`, as the tile and the circle both do: a ring too thin
                 for one glyph comes back from `truncateText` as a bare `…`, which
                 `rankLabelLines` would then prefer over drawing nothing. */
                nameOnly: label
                  ? legibleLines(
                      wrapWords({
                        text: label,
                        availableWidth,
                        measure: measureSvgLabel,
                        maxLines: 1,
                      })
                    )
                  : [],
              });
              if (!lines.length) return null;
              return (
                <Label
                  key={`text_${id}`}
                  transform={labelTransform}
                  style={{ fill: toTextColor(isDark) }}
                >
                  {lines[0]}
                </Label>
              );
            })}
          </g>
        </g>
      </svg>
    </VisualizationContainer>
  );
};
